import * as THREE from 'three'

import EventEmitter from '../Utils/EventEmitter.js'

import ScanModel from '../ScanModel.js'
import { TerrainPlaneBufferGeometry } from '../Geometries/TerrainPlane'
import Terrain from '../Terrain.js'

import topographyVertexShader from '../shaders/topography.vertex.glsl'
import topographyFragmentShader from '../shaders/topography.fragment.glsl'

export default class Droning extends EventEmitter
{
    constructor(_options = {})
    {
        super()

        // Set up
        this.$eventHub = _options.$eventHub
        this.mobile = _options.mobile
        this.camera = _options.camera
        this.sizes = _options.sizes
        this.time = _options.time
        this.cursor = _options.cursor
        this.ui = _options.ui
        this.loader = _options.loader

        this.terrainSize = 10
        this.terrainSubdivision = 256
        this.terrainOffset = 0

        // Container
        this.container = new THREE.Object3D()

        // Camera container
        this.viewContainer = new THREE.Object3D()
        this.container.add(this.viewContainer)

        this.setRunning()
        this.setInteractions()
        this.setTerrain()
        this.setDrone()
        this.setCanvas()
        this.setMap()
        this.setDots()
    }

    /**
     * Set running
     */
    setRunning()
    {
        this.running = false

        this.$eventHub.$on('experience.droning.start', () =>
        {
            this.running = true
        })

        this.$eventHub.$on('experience.droning.stop', () =>
        {
            this.running = false
        })
    }

    /**
     * Set interactions
     */
    setInteractions()
    {
        // Set up
        this.interactions = {}

        this.interactions.game = {}
        this.interactions.game.over = false
        this.interactions.game.fullyOver = false
        this.interactions.game.progress = 0
        this.interactions.game.target = 0.8
        this.interactions.game.interval = null

        /**
         * Limits
         */
        this.interactions.limits = {}
        this.interactions.limits.x = { min: - 4, max: 4 }
        this.interactions.limits.y = { min: 1.29, max: 1.31 }
        this.interactions.limits.z = { min: - 4, max: 4 }

        /**
         * Directions
         */
        this.interactions.directions = {}
        this.interactions.directions.zPos = {}
        this.interactions.directions.zPos.active = false
        this.interactions.directions.zPos.keyCodes = [38, 90, 87, 56]
        this.interactions.directions.zNeg = {}
        this.interactions.directions.zNeg.active = false
        this.interactions.directions.zNeg.keyCodes = [40, 83, 50]
        this.interactions.directions.xPos = {}
        this.interactions.directions.xPos.active = false
        this.interactions.directions.xPos.keyCodes = [37, 81, 52]
        this.interactions.directions.xNeg = {}
        this.interactions.directions.xNeg.active = false
        this.interactions.directions.xNeg.keyCodes = [39, 68, 54]
        this.interactions.directions.yPos = {}
        this.interactions.directions.yPos.active = false
        this.interactions.directions.yPos.keyCodes = [16, 55, 57, 69]
        this.interactions.directions.yNeg = {}
        this.interactions.directions.yNeg.active = false
        this.interactions.directions.yNeg.keyCodes = [32, 49, 51, 65]

        const controlsMapping = {
            up: 'zPos',
            down: 'zNeg',
            left: 'xPos',
            right: 'xNeg',
            shift: 'yPos',
            space: 'yNeg'
        }
        this.$eventHub.$on('experience.droning.uiButtonDown', (_buttonName) =>
        {
            const directionName = controlsMapping[_buttonName]
            const direction = this.interactions.directions[directionName]

            this.$eventHub.$emit('experience.droning.buttonDown', directionName)

            direction.active = true
        })
        this.$eventHub.$on('experience.droning.uiButtonUp', (_buttonName) =>
        {
            const directionName = controlsMapping[_buttonName]
            const direction = this.interactions.directions[directionName]

            this.$eventHub.$emit('experience.droning.buttonUp', directionName)

            direction.active = false
        })

        window.addEventListener('keydown', (_event) =>
        {
            // Not running
            if(!this.running)
            {
                return
            }

            for(const _directionName in this.interactions.directions)
            {
                const direction = this.interactions.directions[_directionName]
                if(direction.keyCodes.indexOf(_event.keyCode) !== -1)
                {
                    _event.preventDefault()

                    this.$eventHub.$emit('experience.droning.buttonDown', _directionName)

                    direction.active = true
                }
            }
        })

        window.addEventListener('keyup', (_event) =>
        {
            // Not running
            if(!this.running)
            {
                return
            }

            for(const _directionName in this.interactions.directions)
            {
                const direction = this.interactions.directions[_directionName]
                if(direction.keyCodes.indexOf(_event.keyCode) !== -1)
                {
                    _event.preventDefault()

                    this.$eventHub.$emit('experience.droning.buttonUp', _directionName)

                    direction.active = false
                }
            }
        })

        /**
         * Speed
         */
        this.interactions.speed = {}
        this.interactions.speed.acceleration = 0.000005
        this.interactions.speed.maxSpeed = 0.02
        this.interactions.speed.dumping = 0.99
        this.interactions.speed.chockDumping = 0.5
        this.interactions.speed.x = 0
        this.interactions.speed.y = 0
        this.interactions.speed.z = 0

        this.time.on('tick', () =>
        {
            // Not running
            if(!this.running)
            {
                return
            }

            // Already won
            if(this.interactions.won)
            {
                return
            }

            // X axis
            if(this.interactions.directions.xPos.active && this.drone.container.position.x < this.interactions.limits.x.max)
            {
                this.interactions.speed.x += this.interactions.speed.acceleration * this.time.delta
            }
            if(this.interactions.directions.xNeg.active && this.drone.container.position.x > this.interactions.limits.x.min)
            {
                this.interactions.speed.x -= this.interactions.speed.acceleration * this.time.delta
            }
            if(Math.abs(this.interactions.speed.x) > this.interactions.speed.maxSpeed)
            {
                this.interactions.speed.x = this.interactions.speed.maxSpeed * Math.sign(this.interactions.speed.x)
            }

            this.interactions.speed.x *= this.interactions.speed.dumping

            // Y axis
            if(this.interactions.directions.yPos.active && this.drone.container.position.y < this.interactions.limits.y.max)
            {
                this.interactions.speed.y += this.interactions.speed.acceleration * this.time.delta
            }
            if(this.interactions.directions.yNeg.active && this.drone.container.position.y > this.interactions.limits.y.min)
            {
                this.interactions.speed.y -= this.interactions.speed.acceleration * this.time.delta
            }
            if(Math.abs(this.interactions.speed.y) > this.interactions.speed.maxSpeed)
            {
                this.interactions.speed.y = this.interactions.speed.maxSpeed * Math.sign(this.interactions.speed.y)
            }

            this.interactions.speed.y *= this.interactions.speed.dumping

            // Z axis
            if(this.interactions.directions.zPos.active && this.drone.container.position.z < this.interactions.limits.z.max)
            {
                this.interactions.speed.z += this.interactions.speed.acceleration * this.time.delta
            }
            if(this.interactions.directions.zNeg.active && this.drone.container.position.z > this.interactions.limits.z.min)
            {
                this.interactions.speed.z -= this.interactions.speed.acceleration * this.time.delta
            }
            if(Math.abs(this.interactions.speed.z) > this.interactions.speed.maxSpeed)
            {
                this.interactions.speed.z = this.interactions.speed.maxSpeed * Math.sign(this.interactions.speed.z)
            }

            this.interactions.speed.z *= this.interactions.speed.dumping
        })

        /**
         * Update
         */
        this.time.on('tick', () =>
        {
            // Not running
            if(!this.running)
            {
                return
            }

            // Already won
            if(this.interactions.won)
            {
                return
            }

            this.drone.container.position.x += this.interactions.speed.x
            this.drone.container.position.y += this.interactions.speed.y
            this.drone.container.position.z += this.interactions.speed.z

            // View
            const targetX = - this.drone.container.position.x * 1
            const targetZ = - this.drone.container.position.z * 1 - 1.8
            // const targetY = - this.drone.container.position.y * 0.95
            this.viewContainer.position.x += (targetX - this.viewContainer.position.x) * 0.05
            this.viewContainer.position.z += (targetZ - this.viewContainer.position.z) * 0.05
            // this.viewContainer.position.y += (targetY - this.viewContainer.position.y) * this.time.delta * 0.003
        })

        this.interactions.game.intervalCallback = () =>
        {
            this.interactions.game.progress = this.canvas.getProgress()

            // Game over
            if(this.interactions.game.progress > this.interactions.game.target)
            {
                this.interactions.game.over = true
                this.interactions.game.progress = 1

                this.$eventHub.$emit('experience.droning.over')

                window.setTimeout(() =>
                {
                    this.interactions.game.fullyOver = true
                }, 2000)

                window.clearInterval(this.interactions.game.interval)
                this.interactions.game.interval = null
            }
        }

        this.interactions.game.interval = window.setInterval(this.interactions.game.intervalCallback, 200)
    }

    /**
     * Set terrain
     */
    setTerrain()
    {
        this.terrain = new Terrain({
            mobile: this.mobile,
            time: this.time,
            loader: this.loader,
            ui: this.ui,
            geometry: new TerrainPlaneBufferGeometry(this.terrainSize, this.terrainSize, this.terrainSubdivision, this.terrainSubdivision, 0), // 4
            texture: this.loader.resources.investigateTerrainTexture
        })

        this.terrain.plane.material.uniforms.uElevationNoiseMultiplier.value = 0.15
        this.terrain.plane.material.uniforms.uColor.value.x = 0.75
        this.terrain.plane.material.uniforms.uColor.value.y = 0.75
        this.terrain.plane.material.uniforms.uColor.value.z = 0.75

        this.terrain.container.position.z = 0.6

        this.viewContainer.add(this.terrain.container)
    }

    /**
     * Set dots
     */
    setDots()
    {
        this.dots = {}

        // Set up
        this.dots.count = 400

        // Geometry
        this.dots.geometry = new THREE.BufferGeometry()
        const position = new Float32Array(this.dots.count * 3)
        const maskPosition = new Float32Array(this.dots.count * 2)
        for(let i = 0; i < this.dots.count; i++)
        {
            let randomX = Math.random()
            let randomZ = Math.random()

            randomX = Math.round(randomX * this.terrainSubdivision) / this.terrainSubdivision
            randomZ = Math.round(randomZ * this.terrainSubdivision) / this.terrainSubdivision

            const x = randomX * this.terrainSize
            const z = randomZ * this.terrainSize
            const y = this.map.canvas.getValueAtPosition(randomX, randomZ)

            position[i * 3 + 0] = x
            position[i * 3 + 1] = y
            position[i * 3 + 2] = z

            maskPosition[i * 2 + 0] = 1 - randomX
            maskPosition[i * 2 + 1] = randomZ
        }

        this.dots.geometry.addAttribute('position', new THREE.BufferAttribute(position, 3))
        this.dots.geometry.addAttribute('maskPosition', new THREE.BufferAttribute(maskPosition, 2))

        // Squares material
        this.dots.squareMaterial = new THREE.ShaderMaterial({
            transparent: true,
            blending: THREE.AdditiveBlending,
            uniforms:
            {
                uRevealPosition: { value: new THREE.Vector2(0, 0) },
                uMask: { value: this.canvas.texture }
            },
            vertexShader: `
                uniform vec2 uRevealPosition;
                uniform sampler2D uMask;

                attribute vec2 maskPosition;

                void main()
                {
                    vec3 modelPosition = (modelMatrix * vec4(position, 1.0)).xyz;
                    float revealDistance = distance(modelPosition.xz, uRevealPosition);

                    float progress = texture2D(uMask, maskPosition).r;

                    // float progress = 6.0 - (revealDistance / 0.3);
                    // progress = clamp(progress, 0.0, 1.0);

                    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
                    gl_PointSize = 8.0 * progress;
                }
            `,
            fragmentShader: `
                void main()
                {
                    gl_FragColor = vec4(1.0, 1.0, 1.0, 0.8);
                }
            `
        })

        // Squares points
        this.dots.squares = new THREE.Points(this.dots.geometry, this.dots.squareMaterial)
        this.dots.squares.position.x = this.terrain.container.position.x - this.terrainSize * 0.5
        this.dots.squares.position.z = this.terrain.container.position.z - this.terrainSize * 0.5
        this.viewContainer.add(this.dots.squares)

        // Stroke material
        this.dots.strokeMaterial = new THREE.ShaderMaterial({
            transparent: true,
            blending: THREE.AdditiveBlending,
            uniforms:
            {
                uRevealPosition: { value: new THREE.Vector2(0, 0) },
                uMask: { value: this.canvas.texture }
            },
            vertexShader: `
                uniform vec2 uRevealPosition;
                uniform sampler2D uMask;

                attribute vec2 maskPosition;

                varying float vProgress;

                void main()
                {
                    vec3 modelPosition = (modelMatrix * vec4(position, 1.0)).xyz;
                    float revealDistance = distance(modelPosition.xz, uRevealPosition);

                    float progress = texture2D(uMask, maskPosition).r;

                    // float progress = 8.0 - (revealDistance / 0.15);
                    // progress = clamp(progress, 0.0, 1.0);
                    vProgress = progress;

                    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
                    // gl_PointSize = 10.0 * progress * 6.0;
                    gl_PointSize = 10.0 * 6.0;
                }
            `,
            fragmentShader: `
                varying float vProgress;

                void main()
                {
                    if(abs(gl_PointCoord.x - 0.5) > 0.015 || gl_PointCoord.y > 0.5 || gl_PointCoord.y < 0.5 * (1.0 - vProgress))
                    {
                        discard;
                    }
                    gl_FragColor = vec4(1.0, 1.0, 1.0, 0.8);
                }
            `
        })

        // Strokes points
        this.dots.strokes = new THREE.Points(this.dots.geometry, this.dots.strokeMaterial)
        this.dots.strokes.position.x = this.terrain.container.position.x - this.terrainSize * 0.5
        this.dots.strokes.position.z = this.terrain.container.position.z - this.terrainSize * 0.5
        this.viewContainer.add(this.dots.strokes)

        // Time tick
        this.time.on('tick', () =>
        {
            const dronePosition = new THREE.Vector3()
            dronePosition.setFromMatrixPosition(this.drone.container.matrixWorld)

            this.dots.squareMaterial.uniforms.uRevealPosition.value.x = dronePosition.x
            this.dots.squareMaterial.uniforms.uRevealPosition.value.y = dronePosition.z

            this.dots.strokeMaterial.uniforms.uRevealPosition.value.x = dronePosition.x
            this.dots.strokeMaterial.uniforms.uRevealPosition.value.y = dronePosition.z
        })
    }

    /**
     * Set drone
     */
    setDrone()
    {
        this.drone = {}

        // Container
        this.drone.container = new THREE.Object3D()
        this.drone.container.position.y = 1.3
        this.drone.container.position.x = 0
        this.drone.container.position.z = - 3
        this.drone.container.scale.x = 0.01
        this.drone.container.scale.y = 0.01
        this.drone.container.scale.z = 0.01
        this.drone.container.rotation.y = - Math.PI * 0.5
        this.viewContainer.add(this.drone.container)

        // Swaying
        this.drone.swaying = new THREE.Object3D()
        this.drone.container.add(this.drone.swaying)

        // Base
        this.drone.base = new ScanModel({
            camera: this.camera,
            sizes: this.sizes,
            time: this.time,
            geometry: this.loader.resources.droneBaseModelObj.geometry,
            revealPosition: { x: -57.66, y: 1.24, z: 18.7 },
            color: { x: 1, y: 0.9, z: 0 } // Yellow
        })
        this.drone.swaying.add(this.drone.base.container)

        // Rotor
        this.drone.rotor = new ScanModel({
            camera: this.camera,
            sizes: this.sizes,
            time: this.time,
            geometry: this.loader.resources.droneRotorModelObj.geometry,
            revealPosition: { x: -57.66, y: 1.24, z: 18.7 },
            color: { x: 1, y: 0.9, z: 0 } // Yellow
        })
        this.drone.swaying.add(this.drone.rotor.container)

        // Back rotor
        this.drone.backRotor = new ScanModel({
            camera: this.camera,
            sizes: this.sizes,
            time: this.time,
            geometry: this.loader.resources.droneBackRotorModelObj.geometry,
            revealPosition: { x: -57.66, y: 1.24, z: 18.7 },
            color: { x: 1, y: 0.9, z: 0 } // Yellow
        })
        this.drone.backRotor.container.position.x = - 9.28
        this.drone.backRotor.container.position.z = - 0.4
        this.drone.swaying.add(this.drone.backRotor.container)

        // Time tick event
        this.time.on('tick', () =>
        {
            // Not running
            if(!this.running)
            {
                return
            }

            this.drone.rotor.container.rotation.y = this.time.elapsed * 0.015
            this.drone.backRotor.container.rotation.z = this.time.elapsed * 0.02
            // this.drone.swaying.rotation.x = Math.sin(this.time.elapsed * 0.0002) * 0.02
            // this.drone.swaying.rotation.z = Math.sin(this.time.elapsed * 0.000678) * 0.005

            this.drone.swaying.rotation.x = - this.interactions.speed.x * 40
            this.drone.swaying.rotation.z = - this.interactions.speed.z * 40

            // this.drone.swaying.position.x = Math.sin(this.time.elapsed * 0.00023) * 0.2
            // this.drone.swaying.position.z = Math.sin(this.time.elapsed * 0.000778) * 0.5
            // this.drone.swaying.position.y = Math.sin(this.time.elapsed * 0.000578) * 0.3
        })
    }

    /**
     * Set canvas
     */
    setCanvas()
    {
        this.canvas = {}

        this.canvas.size = 16
        this.canvas.radiusRatio = 0.16
        this.canvas.alpha = 0.1
        this.canvas.radius = this.canvas.size * this.canvas.radiusRatio

        // Element
        this.canvas.$element = document.createElement('canvas')
        this.canvas.$element.width = this.canvas.size
        this.canvas.$element.height = this.canvas.size
        // this.canvas.$element.style.width = '200px'
        // this.canvas.$element.style.height = '200px'
        // this.canvas.$element.style.position = 'fixed'
        // this.canvas.$element.style.top = '0'
        // this.canvas.$element.style.left = '0'
        // document.body.appendChild(this.canvas.$element)

        // Context
        this.canvas.context = this.canvas.$element.getContext('2d')

        // Fill with black
        this.canvas.context.fillStyle = '#000000'
        this.canvas.context.fillRect(0, 0, this.canvas.size, this.canvas.size)

        // Three texture from canvas
        this.canvas.texture = new THREE.Texture(this.canvas.$element)

        // Get value at position
        this.canvas.getProgress = () =>
        {
            const data = this.canvas.context.getImageData(0, 0, this.canvas.size, this.canvas.size).data

            let total = 0

            for(let i = 0; i < data.length; i += 4)
            {
                total += data[i]
            }

            return total / (data.length / 4) / 255
        }

        // Time tick event
        this.time.on('tick', () =>
        {
            // Not running
            if(!this.running)
            {
                return
            }

            // Not over
            if(!this.interactions.game.over)
            {
                // Draw circle at drone position
                this.canvas.context.fillStyle = '#ffffff'
                this.canvas.context.globalAlpha = this.canvas.alpha

                const x = (1 - (this.drone.container.position.x / this.terrainSize + 0.5)) * this.canvas.$element.width
                const y = (1 - (this.drone.container.position.z / this.terrainSize + 0.5)) * this.canvas.$element.height

                this.canvas.context.beginPath()
                this.canvas.context.arc(x, y, this.canvas.radius, 0, Math.PI * 2)
                this.canvas.context.fill()

                this.canvas.texture.needsUpdate = true
            }

            // Not fully over
            else if(!this.interactions.game.fullyOver)
            {
                // Fill everything
                this.canvas.context.fillStyle = '#ffffff'
                this.canvas.context.globalAlpha = 0.015

                this.canvas.context.fillRect(0, 0, this.canvas.size, this.canvas.size)

                this.canvas.texture.needsUpdate = true
            }
        })
    }

    /**
     * Set map
     */
    setMap()
    {
        this.map = {}

        this.map.contentOffset = 0.1

        /**
         * Canvas
         */
        this.map.canvas = {}

        this.map.canvas.size = this.terrainSubdivision

        this.map.canvas.$element = document.createElement('canvas')
        this.map.canvas.$element.width = this.map.canvas.size
        this.map.canvas.$element.height = this.map.canvas.size
        // this.map.canvas.$element.style.position = 'fixed'
        // this.map.canvas.$element.style.top = '0'
        // this.map.canvas.$element.style.left = '0'
        // document.body.appendChild(this.map.canvas.$element)

        this.map.canvas.context = this.map.canvas.$element.getContext('2d')

        this.map.canvas.context.fillStyle = '#ff0000'
        this.map.canvas.context.fillRect(0, 0, this.map.canvas.size, this.map.canvas.size)

        this.map.canvas.context.drawImage(this.loader.resources.investigateTerrainTexture, 0, 0, this.map.canvas.size, this.map.canvas.size)

        this.map.canvas.getValueAtPosition = (_x, _y) =>
        {
            const x = Math.round(_x * this.map.canvas.size)
            const y = Math.round(_y * this.map.canvas.size)

            const data = this.map.canvas.context.getImageData(x, y, 1, 1).data

            // this.map.canvas.context.fillStyle = '#ff0000'
            // this.map.canvas.context.fillRect(x, y, 5, 5)

            return data[0] / 255
        }

        /**
         * Container
         */
        this.map.container = new THREE.Object3D()
        this.map.container.position.y = 1
        this.map.container.position.x = 0.4
        this.map.container.rotation.x = - Math.PI * 0.05
        this.container.add(this.map.container)

        const resize = () =>
        {
            this.map.container.position.x = (this.sizes.viewport.width / this.sizes.viewport.height) * 0.23 - 0.02

            if(this.sizes.viewport.width <= 1023)
            {
                this.map.container.scale.set(0.2, 0.2, 0.2)
                this.map.container.position.z = - 1.65
            }
            else
            {
                this.map.container.scale.set(0.25, 0.25, 0.25)
                this.map.container.position.z = - 1.6
            }
        }

        this.sizes.on('resize', resize)

        resize()

        /**
         * Pivot
         */
        this.map.pivot = new THREE.Object3D()
        this.map.container.add(this.map.pivot)

        /**
         * Floor
         */
        this.map.floor = new THREE.Mesh(new THREE.PlaneBufferGeometry(1, 1, 1, 1), new THREE.MeshBasicMaterial({ color: 0xffffff, wireframe: true, transparent: true, opacity: 0.2 }))
        this.map.floor.rotation.x = - Math.PI * 0.5
        this.map.pivot.add(this.map.floor)

        /**
         * Topography
         */
        this.map.topography = {}
        this.map.topography.layers = []
        this.map.topography.height = 0.15
        this.map.topography.colors = [
            0x237681,
            0x79a25c,
            0xb8c069,
            0xefe17b,
            0xca9e64,
            0xa75f4a
        ]

        this.map.topography.container = new THREE.Object3D()
        this.map.pivot.add(this.map.topography.container)

        for(let i = 0; i < 6; i++)
        {
            const layer = {}
            const texture = new THREE.Texture(this.loader.resources[`investigateTopographyLayer${i + 1}Texture`])
            texture.minFilter = THREE.NearestFilter
            texture.minFilter = THREE.NearestMipMapNearestFilter
            texture.minFilter = THREE.NearestMipMapLinearFilter
            texture.minFilter = THREE.LinearFilter
            // texture.minFilter = THREE.LinearMipMapNearestFilter
            // texture.minFilter = THREE.LinearMipMapLinearFilter
            texture.needsUpdate = true

            layer.geometry = new THREE.PlaneBufferGeometry(1, 1, 10, 10)
            layer.material = new THREE.MeshBasicMaterial({ color: 0xffffff, wireframe: true, transparent: true, opacity: 0.1 })
            layer.material = new THREE.ShaderMaterial({
                transparent: true,
                blending: THREE.AdditiveBlending,
                flatShading: true,
                uniforms:
                {
                    uColor: { value: new THREE.Color(this.map.topography.colors[i]) },
                    uMask: { value: this.canvas.texture },
                    uTexture: { value: texture }
                },
                vertexShader: topographyVertexShader,
                fragmentShader: topographyFragmentShader
            })
            layer.mesh = new THREE.Mesh(layer.geometry, layer.material)
            layer.mesh.rotation.x = - Math.PI * 0.5
            layer.mesh.rotation.z = Math.PI
            layer.mesh.position.y = i * (this.map.topography.height / 6) + this.map.contentOffset
            layer.mesh.renderOrder = 9999

            this.map.topography.container.add(layer.mesh)

            this.map.topography.layers.push(layer)
        }

        /**
         * Pin
         */
        this.map.pin = {}

        this.map.pin.container = new THREE.Object3D()
        this.map.pivot.add(this.map.pin.container)

        this.map.pin.geometry = new THREE.CircleBufferGeometry(this.mobile ? 0.02 : 0.01, 12)
        this.map.pin.material = new THREE.MeshBasicMaterial({ color: 0xffe600 })
        this.map.pin.mesh = new THREE.Mesh(this.map.pin.geometry, this.map.pin.material)
        this.map.pin.mesh.rotation.x = - Math.PI * 0.5
        this.map.pin.container.add(this.map.pin.mesh)

        this.time.on('tick', () =>
        {
            // Not running
            if(!this.running)
            {
                return
            }

            // Pin
            this.map.pin.container.position.x = this.drone.container.position.x / this.terrainSize
            this.map.pin.container.position.y = this.drone.container.position.y / this.terrainSize + this.map.contentOffset
            this.map.pin.container.position.z = this.drone.container.position.z / this.terrainSize

            // Rotation
            if(this.camera.type === 'guided')
            {
                const panX = this.camera.pan.value.x / this.camera.pan.amplitude.x
                const panY = this.camera.pan.value.y / this.camera.pan.amplitude.y

                const positionX = this.drone.container.position.x / this.terrainSize
                const positionY = this.drone.container.position.z / this.terrainSize

                this.map.pivot.rotation.z = (- panX + positionX) * 0.25
                this.map.pivot.rotation.x = (- panY - positionY) * 0.25
            }
        })

        /**
         * Beam cap
         */
        this.map.beamCap = {}
        this.map.beamCap.geometry = new THREE.CircleBufferGeometry(0.005, 12)
        this.map.beamCap.material = new THREE.MeshBasicMaterial({ color: 0xffffff, transparent: true, opacity: 0.75, depthTest: false })
        this.map.beamCap.mesh = new THREE.Mesh(this.map.beamCap.geometry, this.map.beamCap.material)
        this.map.beamCap.mesh.rotation.x = - Math.PI * 0.5
        this.map.pin.container.add(this.map.beamCap.mesh)

        /**
         * Beam
         */
        this.map.beam = {}
        this.map.beam.geometry = new THREE.PlaneBufferGeometry(0.005, 1, 1, 1)
        this.map.beam.geometry.translate(0, - 0.5, 0)
        this.map.beam.material = new THREE.MeshBasicMaterial({ color: 0xffffff, transparent: true, opacity: 0.2, depthTest: false })
        this.map.beam.mesh = new THREE.Mesh(this.map.beam.geometry, this.map.beam.material)
        this.map.beam.mesh.rotation.y = Math.PI
        this.map.beam.mesh.scale.y = 0.2
        this.map.pin.container.add(this.map.beam.mesh)

        this.time.on('tick', () =>
        {
            // Not running
            if(!this.running)
            {
                return
            }

            const x = this.drone.container.position.x / this.terrainSize + 0.5
            const y = this.drone.container.position.z / this.terrainSize + 0.5
            const elevation = this.map.canvas.getValueAtPosition(x, y)
            const scale = this.map.pin.container.position.y - elevation * this.map.topography.height - this.map.contentOffset

            this.map.beam.mesh.scale.y = scale

            this.map.beamCap.mesh.position.y = - scale
        })

        /**
         * Progress
         */
        this.map.progress = {}
        this.map.progress.width = 0.01
        this.map.progress.progress = 0

        this.map.progress.container = new THREE.Object3D()
        this.map.progress.container.rotation.y = - Math.PI * 0.5
        this.map.progress.container.position.z = - (0.5 + 0.1)
        this.map.pivot.add(this.map.progress.container)

        this.map.progress.base = new THREE.Mesh(new THREE.PlaneBufferGeometry(this.map.progress.width, 1, 1, 1), new THREE.MeshBasicMaterial({ color: 0xffffff, wireframe: true, transparent: true, opacity: 0.2 }))
        this.map.progress.base.rotation.x = - Math.PI * 0.5
        this.map.progress.container.add(this.map.progress.base)

        this.map.progress.fill = new THREE.Mesh(new THREE.PlaneBufferGeometry(this.map.progress.width, 1, 1, 1), new THREE.MeshBasicMaterial({ color: 0xffe600, transparent: true, opacity: 1 }))
        this.map.progress.fill.geometry.translate(0, - 0.5, 0)
        this.map.progress.fill.rotation.x = - Math.PI * 0.5
        this.map.progress.fill.position.y = 0.05
        this.map.progress.fill.position.z = - 0.5
        this.map.progress.fill.scale.y = 0
        this.map.progress.container.add(this.map.progress.fill)

        this.map.progress.text = {}
        this.map.progress.text.width = 50
        this.map.progress.text.height = 20
        this.map.progress.text.scale = 0.003
        this.map.progress.text.$canvas = document.createElement('canvas')
        this.map.progress.text.$canvas.width = this.map.progress.text.width
        this.map.progress.text.$canvas.height = this.map.progress.text.height
        this.map.progress.text.$canvas.style.position = 'fixed'
        this.map.progress.text.$canvas.style.top = 0
        this.map.progress.text.$canvas.style.left = 0
        this.map.progress.text.$canvas.style.zIndex = 80
        this.map.progress.text.value = '0%'

        this.map.progress.text.context = this.map.progress.text.$canvas.getContext('2d')
        this.map.progress.text.context.textBaseline = 'bottom'
        this.map.progress.text.context.textAlign = 'left'
        this.map.progress.text.context.font = '22px "Blender Pro"'
        this.map.progress.text.context.fillStyle = '#ffe600'

        this.map.progress.text.texture = new THREE.Texture(this.map.progress.text.$canvas)
        this.map.progress.text.texture.minFilter = THREE.LinearFilter

        this.map.progress.text.material = new THREE.MeshBasicMaterial({ map: this.map.progress.text.texture, transparent: true })
        this.map.progress.text.geometry = new THREE.PlaneBufferGeometry(this.map.progress.text.width * this.map.progress.text.scale, this.map.progress.text.height * this.map.progress.text.scale, 1, 1)
        this.map.progress.text.mesh = new THREE.Mesh(this.map.progress.text.geometry, this.map.progress.text.material)
        this.map.progress.text.mesh.rotation.order = 'YXZ'
        this.map.progress.text.mesh.rotation.y = - Math.PI * 0.5
        this.map.progress.text.mesh.rotation.x = - Math.PI * 0.2
        this.map.progress.text.mesh.position.y = 0.12
        this.map.progress.container.add(this.map.progress.text.mesh)

        this.time.on('tick', () =>
        {
            // Not running
            if(!this.running)
            {
                return
            }

            // Clamped and eased progress
            const progress = Math.min(this.interactions.game.progress / this.interactions.game.target, 1)
            this.map.progress.progress += (progress - this.map.progress.progress) * 0.01 * this.time.delta
            this.map.progress.fill.scale.y = this.map.progress.progress
            this.map.progress.text.mesh.position.z = this.map.progress.progress - 0.5 + (this.map.progress.text.width * this.map.progress.text.scale * 0.3)

            // Text
            const value = `${Math.round(this.map.progress.progress * 100)}%`
            if(value !== this.map.progress.text.value)
            {
                // Update canvas and texture
                this.map.progress.text.context.clearRect(0, 0, this.map.progress.text.width, this.map.progress.text.height)
                this.map.progress.text.context.fillText(value, 0, this.map.progress.text.height)
                this.map.progress.text.texture.needsUpdate = true

                // Save value
                this.map.progress.text.value = value
            }
        })
    }

    /**
     * Reset
     */
    reset()
    {
        this.interactions.game.over = false
        this.interactions.game.fullyOver = false
        this.interactions.game.progress = 0

        if(!this.interactions.game.interval)
        {
            this.interactions.game.interval = window.setInterval(this.interactions.game.intervalCallback, 200)
        }

        this.drone.container.position.y = 1.3
        this.drone.container.position.x = 0
        this.drone.container.position.z = - 3

        this.canvas.context.fillStyle = '#000000'
        this.canvas.context.globalAlpha = 1
        this.canvas.context.fillRect(0, 0, this.canvas.size, this.canvas.size)
        this.canvas.texture.needsUpdate = true
    }
}



// WEBPACK FOOTER //
// ./src/components/app-experience/Experience/Interactions/Droning.js